import { ApolloError } from '@apollo/client';

import {
  useErpFundingOperatorQuery,
  useErpFundingIntakesQuery,
  useErpFundingIntakesTotalQuery,
} from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import { useDofinPermissions } from './useDofinPermissions';

/**
 * ТЗ 0024 — карточка оператора.
 *
 * Корневое поле `fundingOperator(id)` существовало и не вызывалось ниоткуда,
 * а список тянул по сети одиннадцать полей, которые маппер хука выбрасывал:
 * критерии участника, ссылки на проект и документы, состояние робота и
 * историю окон набора. Здесь они наконец доходят до экрана.
 */
export const useFundingOperator = (id?: string | null) => {
  const { data, loading, error, refetch } = useErpFundingOperatorQuery({
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return {
    operator: data?.fundingOperator ?? null,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

/**
 * «Чей набор открывается на днях» — по всем операторам сразу.
 *
 * Запрос пропускается для ролей без доступа: `PermissionDenied` летит в
 * `errors`, то есть отклоняет промис, а не возвращает `customErrors`.
 */
export interface IIntakeFilters {
  operatorId?: string;
  /** Фазы окна: `upcoming` / `running` / `ended` / `cancelled` / `unknown`.
   *  Считает СЕРВЕР — тем же выражением, что рисует колонку.
   *
   *  Список, а не строка: сегмент «Рабочие» это `upcoming ∪ running ∪
   *  unknown`, и одним значением он не выражается. */
  phase?: string[];
  /** `confirmed` / `unconfirmed` — подтвердил ли человек набор. */
  confirmation?: string;
  startsFrom?: string;
  startsTo?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

/** Страница списка наборов. Их 123 и станет больше. */
export const INTAKES_PAGE_SIZE = 50;

/**
 * Список окон набора с фильтрами (спека 0055).
 *
 * 🔴 Здесь стоял `useUpcomingIntakes(withinDays)` — не список, а один срез:
 * живые окна в пределах горизонта. Из 123 окон вкладка показывала 18, а 105
 * не показывались никогда. Горизонт при этом ничего не менял: 14, 30, 60 и 90
 * дней давали одни и те же восемнадцать строк, и человек двигал переключатель
 * без всякого эффекта.
 */
export const useFundingIntakes = (filters: IIntakeFilters = {}) => {
  const { canRead } = useDofinPermissions();
  const variables = {
    operatorId: filters.operatorId || undefined,
    phase: filters.phase || undefined,
    confirmation: filters.confirmation || undefined,
    startsFrom: filters.startsFrom || undefined,
    startsTo: filters.startsTo || undefined,
    search: filters.search || undefined,
  };
  const { data, loading, error, refetch } = useErpFundingIntakesQuery({
    variables: {
      ...variables,
      limit: filters.limit ?? INTAKES_PAGE_SIZE,
      offset: filters.offset ?? 0,
    },
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  // ⚠️ Счётчик берёт ТЕ ЖЕ переменные, без `limit`/`offset`. Собранный
  // отдельно, он ответил бы на другой вопрос, и «показано 25 из 40» стало бы
  // неправдой.
  const totalQuery = useErpFundingIntakesTotalQuery({
    variables,
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });

  return {
    intakes: data?.fundingIntakes ?? [],
    total: totalQuery.data?.fundingIntakesTotal ?? 0,
    loading,
    // Отказ сервера НЕЛЬЗЯ отдавать пустым списком: экран напишет «наборов
    // нет», и менеджер спокойно закроет вкладку.
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};
